import { ReactNode, memo } from 'react';

import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { useStoreSettings } from 'data-layer';
import { twMerge } from 'tailwind-merge';

import { CurrencySymbol } from '../../atoms';

const Price = memo(
  ({
    currency,
    value,
    discountPrice,
    classNameDiscount,
    styleDiscount,
    discontPosition = 'right',
  }: PriceProps) => {
    const withDiscount =
      discountPrice !== undefined && typeof discountPrice === 'number' && discountPrice > 0;
    const isValueNumber = !Array.isArray(value) && typeof value === 'number';
    const formattedValue = isValueNumber ? value?.toFixed(2) : value;

    let symbol: ReactNode = '';
    switch (currency) {
      case 'GBP':
      case '£':
        symbol = '£';
        return (
          <span
            className={twMerge('flex gap-2', discontPosition === 'left' ? 'flex-row-reverse' : '')}
          >
            {addDiscountPrice(symbol, discountPrice)}
            {addNormalPrice({
              symbol,
              price: formattedValue,
              withDiscount,
              classNameDiscount,
              styleDiscount,
            })}
          </span>
        );
      case 'USD':
      case '$':
        symbol = '$';
        return (
          <span
            className={twMerge('flex gap-2', discontPosition === 'left' ? 'flex-row-reverse' : '')}
          >
            {addDiscountPrice(symbol, discountPrice)}
            {addNormalPrice({
              symbol,
              price: formattedValue,
              withDiscount,
              classNameDiscount,
              styleDiscount,
            })}
          </span>
        );
      case 'PLN':
      case 'zł':
        symbol = <span className="lowercase">zł</span>;
        return (
          <span
            className={twMerge('flex gap-2', discontPosition === 'left' ? 'flex-row-reverse' : '')}
          >
            {addDiscountPrice(symbol, discountPrice)}
            {addNormalPrice({
              symbol,
              price: formattedValue,
              withDiscount,
              reverse: true,
              classNameDiscount,
              styleDiscount,
            })}
          </span>
        );
      case 'EUR':
      case '€':
      case undefined:
        symbol = `\u20ac`;
        return (
          <span
            className={twMerge('flex gap-2', discontPosition === 'left' ? 'flex-row-reverse' : '')}
          >
            {addDiscountPrice(symbol, discountPrice)}
            {addNormalPrice({
              symbol,
              price: formattedValue,
              withDiscount,
              classNameDiscount,
              styleDiscount,
            })}
          </span>
        );
      default:
        symbol = `${currency} `;
        return (
          <span
            className={twMerge('flex gap-2', discontPosition === 'left' ? 'flex-row-reverse' : '')}
          >
            {addDiscountPrice(symbol, discountPrice)}
            {addNormalPrice({
              symbol,
              price: formattedValue,
              withDiscount,
              classNameDiscount,
              styleDiscount,
            })}
          </span>
        );
    }
  },
);

const PriceWithStoreCurrency = memo(({ value }: { value: any }) => {
  const { data: storeSettings } = useStoreSettings();

  return (
    <>
      <CurrencySymbol currency={storeSettings?.currency} />
      {parseFloat(String(value || 0)).toFixed(2)}
    </>
  );
});

export { Price, PriceWithStoreCurrency };

interface PriceProps {
  currency?: string;
  value: ReactNode;
  discountPrice?: number | null;
  classNameDiscount?: string;
  styleDiscount?: CSSProperties | undefined;
  discontPosition?: 'left' | 'right';
}

const addDiscountPrice = (symbol: ReactNode, discountPrice?: number | null, reverse = false) => {
  if (discountPrice !== undefined && typeof discountPrice === 'number' && discountPrice > 0) {
    return (
      <>
        {!reverse && symbol}
        {discountPrice?.toFixed(2)}
        {reverse && symbol}
      </>
    );
  }
  return '';
};

const addNormalPrice = ({
  symbol,
  price,
  withDiscount = false,
  reverse = false,
  classNameDiscount,
  styleDiscount,
}: {
  symbol: ReactNode;
  price?: any;
  withDiscount: boolean;
  reverse?: boolean;
  classNameDiscount?: string;
  styleDiscount?: CSSProperties | undefined;
}) => {
  if (price) {
    return (
      <>
        <span
          style={styleDiscount}
          className={withDiscount ? twMerge('line-through', classNameDiscount) : ''}
        >
          {!reverse && symbol}
          {price}
          {reverse && symbol}
        </span>
      </>
    );
  }
  return '';
};
